import React, { useState, useEffect } from 'react';
import { PairingDisplay, MatchResult } from '../components/Match';
import { useMatchStore } from '../stores/matchStore';
import { useQueueStore } from '../stores/queueStore';

const MatchPage: React.FC = () => {
  const { inProgressMatch, startMatch, loadInProgressMatch, isLoading } = useMatchStore();
  const { queue } = useQueueStore();
  const [showMatchResult, setShowMatchResult] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // 页面加载时检查是否有进行中的比赛
  useEffect(() => {
    loadInProgressMatch();
  }, [loadInProgressMatch]);

  const handleMatchStart = async () => {
    console.log('MatchPage: 开始比赛');
    
    if (!queue) {
      console.error('队列信息不存在');
      return;
    }

    try {
      await startMatch(queue.players);
      console.log('比赛已开始，状态已保存到数据库');
    } catch (error) {
      console.error('开始比赛失败:', error);
    }
  };

  const handleEnterResult = () => {
    console.log('进入结果录入界面');
    setShowMatchResult(true);
  };

  const handleResultSaved = () => {
    console.log('比赛结果已保存');
    setShowMatchResult(false);
    
    // 显示成功消息
    setShowSuccessMessage(true);
    
    // 3秒后隐藏成功消息
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };

  const handleCancel = () => {
    setShowMatchResult(false);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900">比赛管理</h1>
        <p className="text-gray-600 mt-1">生成配对、进行比赛并记录结果</p>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto">
        {/* 成功消息 */}
        {showSuccessMessage && (
          <div className="mb-4 p-4 bg-green-100 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <div className="text-green-600 text-sm font-medium">
                ✅ 比赛结果已成功保存！可以查看"比赛历史"或开始下一场比赛。
              </div>
            </div>
          </div>
        )}
        
        {!showMatchResult ? (
          <div className="space-y-4">
            {/* 如果有进行中的比赛，显示比赛信息 */}
            {inProgressMatch ? (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-800">比赛进行中</h2>
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                    进行中
                  </span>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-center space-x-8">
                    {/* 队伍1 */}
                    <div className="text-center">
                      <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-4 min-w-[120px]">
                        <div className="text-blue-800 font-semibold mb-2">开室</div>
                        <div className="space-y-1">
                          {inProgressMatch.pairing.team1.map((player) => (
                            <div key={player.id} className="text-sm">
                              <div className="font-medium">{player.name}</div>
                              <div className="text-xs text-blue-600">#{player.number}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* VS */}
                    <div className="text-2xl font-bold text-gray-400">🤝</div>

                    {/* 队伍2 */}
                    <div className="text-center">
                      <div className="bg-green-100 border-2 border-green-300 rounded-lg p-4 min-w-[120px]">
                        <div className="text-green-800 font-semibold mb-2">闭室</div>
                        <div className="space-y-1">
                          {inProgressMatch.pairing.team2.map((player) => (
                            <div key={player.id} className="text-sm">
                              <div className="font-medium">{player.name}</div>
                              <div className="text-xs text-green-600">#{player.number}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <button
                    onClick={handleEnterResult}
                    disabled={isLoading}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-3 px-8 rounded-lg transition-colors"
                  >
                    录入比赛结果
                  </button>
                </div>

                <div className="mt-4 text-center text-sm text-gray-500">
                  比赛开始时间：{new Date(inProgressMatch.playedAt).toLocaleString('zh-CN')}
                </div>
              </div>
            ) : (
              /* 没有进行中的比赛，显示配对界面 */
              <PairingDisplay onMatchStart={handleMatchStart} />
            )}
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-blue-800 text-sm">
                💡 <strong>提示：</strong>生成配对后点击"开始比赛"，比赛进行中所有人都能看到配对信息。
                比赛结束后点击"录入比赛结果"保存结果。
              </div>
            </div>
          </div>
        ) : inProgressMatch ? (
          <MatchResult
            match={inProgressMatch}
            onResultSaved={handleResultSaved}
            onCancel={handleCancel}
          />
        ) : (
          <div className="bg-red-100 p-4 rounded">
            错误: 没有进行中的比赛
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchPage;